/**
 * 批次與帳號池：勾選／全部進房／全部離開，以及憑證池的列表、註冊、取用、改名、刪除。
 * 同一種責任：對一組帳號做 N 次序列操作、逐顆回報。單顆身分的開通在 `useIdentityActions`。
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import {
  MAX_POOL_REGISTER,
  POOL_REGISTER_GAP_MS,
} from '@chat-rea/console-ui';
import type {
  Account,
  Conversation,
  NoticeKind,
  PoolAccount,
  PoolRegisterOutcome,
  PoolServiceFilter,
  PushNoticeInput,
  Service,
  UseInboxResult,
} from '@chat-rea/console-ui';
import { client } from '../api';
import { describeOpeningFailure } from './opening-failure';
import {
  DEFAULT_SERVICE,
  LEAVE_GAP_MS,
  poolRegisterNotices,
  type ConsoleActions,
  type LeaveAllFailure,
  type LeaveAllProgress,
  type LeaveAllResult,
  type OpeningAttempt,
} from './consoleActions.shared';

function errMessage(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

export interface BatchActionsDeps {
  inbox: UseInboxResult;
  conversations: Conversation[];
  activeId: string | null;
  currentService: Service;
  targetRoom: string;
  setBusy: Dispatch<SetStateAction<boolean>>;
  setActionError: (message: string | null) => void;
  setNotice: (message: string | null, kind?: NoticeKind) => void;
  pushNotice: (input: PushNoticeInput) => void;
  /** 開通一顆身分（池內帳號入口要用它）；成功 true。 */
  runOpening: (attempt: OpeningAttempt) => Promise<boolean>;
  /**
   * 上一次開通的**原始錯誤訊息**（沒有就 null）。
   * `runOpening` 只回成功／失敗，真正的原因留在 `opening`；這支讓帳號頁那條訊息
   * 講的是「這次失敗自己的原因」，而不是去猜上方橫幅。
   */
  openingError?: () => string | null;
}

/** 對外只回 `ConsoleActions` 的這幾個欄位（型別用 Pick，避免兩份宣告走鐘）。 */
export type BatchActions = Pick<
  ConsoleActions,
  | 'sessionPicked'
  | 'toggleSessionPick'
  | 'joinSessions'
  | 'leaveAll'
  | 'leaveAllProgress'
  | 'leaveAllResult'
  | 'poolAccounts'
  | 'poolLoading'
  | 'poolError'
  | 'poolRegistering'
  | 'poolResults'
  | 'poolRegisterError'
  | 'poolCooldownUntil'
  | 'poolFilter'
  | 'setPoolFilter'
  | 'poolRegisterService'
  | 'setPoolRegisterService'
  | 'loadPool'
  | 'registerPool'
  | 'usePoolAccount'
  | 'deletePoolAccount'
  | 'renameAccount'
>;

export function useBatchActions({
  inbox,
  conversations,
  activeId,
  currentService,
  targetRoom,
  setBusy,
  setActionError,
  setNotice,
  pushNotice,
  runOpening,
  openingError,
}: BatchActionsDeps): BatchActions {
  const { refresh, select } = inbox;

  /** `AccountPool` 的勾選（批次進房的對象）；沒勾＝全部。 */
  const [sessionPicked, setSessionPicked] = useState<string[]>([]);
  /** 「全部離開」的進度與結算（接 `AccountPool` 的 leaveAllProgress／leaveAllResult）。 */
  const [leaveAllProgress, setLeaveAllProgress] = useState<LeaveAllProgress | null>(null);
  const [leaveAllResult, setLeaveAllResult] = useState<LeaveAllResult | null>(null);

  /** 「全部離開」的重入鎖：state 更新是非同步的，連點兩下只有 ref 擋得住（否則同顆送兩次 DELETE）。 */
  const leavingRef = useRef(false);
  /** 批次動作（進房／離開）要讀最新名單與勾選，不能吃 render 當下的閉包。 */
  const batchRef = useRef({ conversations, picked: sessionPicked, activeId });
  batchRef.current = { conversations, picked: sessionPicked, activeId };

  /** 勾選／取消一顆身分（批次動作的對象；沒勾＝全部）。 */
  const toggleSessionPick = useCallback((id: string) => {
    setSessionPicked((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  }, []);

  /** 「全部進房」：勾選的（沒勾＝全部）依序送進同一間房；單顆失敗不中斷，最後只回報成功幾顆。 */
  const joinSessions = useCallback(
    async (room: string) => {
      const { conversations: list, picked } = batchRef.current;
      const targets =
        picked.length > 0 ? list.filter((item) => picked.includes(item.id)) : list;
      if (!room || targets.length === 0) return;
      setBusy(true);
      setActionError(null);
      try {
        let joined = 0;
        const reasons: string[] = [];
        for (let index = 0; index < targets.length; index += 1) {
          const target = targets[index]!;
          try {
            await client.joinRoom(target.id, room);
            joined += 1;
          } catch (err) {
            reasons.push(`${target.nickname}（${errMessage(err)}）`);
          }
          if (index < targets.length - 1) {
            await new Promise((resolve) => setTimeout(resolve, LEAVE_GAP_MS));
          }
        }
        await refresh();
        setNotice(
          reasons.length === 0
            ? `已把 ${joined} 個帳號帶進 ${room}`
            : `進了 ${joined}/${targets.length} 個；失敗：${reasons.join('、')}`,
        );
      } finally {
        setBusy(false);
      }
    },
    [refresh, setActionError, setBusy, setNotice],
  );

  /**
   * 「全部離開」：逐一 `close()`（= `DELETE /api/sessions/:id`），序列、間隔 `LEAVE_GAP_MS`，**不刪帳號**。
   * 逐顆失敗不中斷，最後給 `leaveAllResult` 並 `refresh()` 對帳（被關掉的不該還留在清單上）。
   */
  const leaveAll = useCallback(async () => {
    if (leavingRef.current) return;
    const targets = batchRef.current.conversations.map((item) => ({
      id: item.id,
      nickname: item.nickname,
    }));
    if (targets.length === 0) {
      setLeaveAllResult({ total: 0, left: 0, failed: [] });
      return;
    }

    leavingRef.current = true;
    setBusy(true);
    setActionError(null);
    setLeaveAllResult(null);
    setLeaveAllProgress({ index: 1, total: targets.length, nickname: targets[0]!.nickname });
    const failed: LeaveAllFailure[] = [];
    let left = 0;
    try {
      for (let index = 0; index < targets.length; index += 1) {
        const target = targets[index]!;
        setLeaveAllProgress({ index: index + 1, total: targets.length, nickname: target.nickname });
        try {
          await client.close(target.id);
          left += 1;
          if (target.id === batchRef.current.activeId) select(null);
        } catch (err) {
          failed.push({ accountId: target.id, nickname: target.nickname, reason: errMessage(err) });
        }
        if (index < targets.length - 1) {
          await new Promise((resolve) => setTimeout(resolve, LEAVE_GAP_MS));
        }
      }
      setLeaveAllResult({ total: targets.length, left, failed });
      setSessionPicked([]);
      setNotice(`已離開 ${left} 顆身分；帳號沒被刪，帳號池還在`);
      // 有失敗就不靜默：上方橫幅留一句，帳號頁的結算句另有逐顆原因。
      if (failed.length > 0) {
        setActionError(
          `${failed.length} 顆沒掛下線：${failed.map((item) => `${item.nickname}（${item.reason}）`).join('、')}`,
        );
      }
      await refresh();
    } finally {
      setLeaveAllProgress(null);
      setBusy(false);
      leavingRef.current = false;
    }
  }, [refresh, select, setActionError, setBusy, setNotice]);

  /** 帳號池（憑證池）：`GET /api/accounts`，跟已建好的 session 是兩件事；註冊節流（≤3／≥20 秒）前端也記一份。 */
  const [poolAccounts, setPoolAccounts] = useState<PoolAccount[]>([]);
  const [poolLoading, setPoolLoading] = useState(false);
  const [poolError, setPoolError] = useState<string | null>(null);
  const [poolRegistering, setPoolRegistering] = useState(false);
  const [poolResults, setPoolResults] = useState<PoolRegisterOutcome[]>([]);
  const [poolRegisterError, setPoolRegisterError] = useState<string | null>(null);
  const [poolCooldownUntil, setPoolCooldownUntil] = useState<number | null>(null);
  /** 帳號池要看哪個服務（預設全部，ek21 與 UT 都不會被藏起來）／註冊打哪個服務。 */
  const [poolFilter, setPoolFilter] = useState<PoolServiceFilter>('all');
  const [poolRegisterService, setPoolRegisterService] = useState<Service>(DEFAULT_SERVICE);

  const poolReqRef = useRef(0);

  /** 帳號池列表。刻意**不帶 service**：兩個服務的帳號都要看得到，才不會切了身分就以為另一邊不見了。 */
  const loadPool = useCallback(async () => {
    const token = (poolReqRef.current += 1);
    setPoolLoading(true);
    try {
      const page = await client.listAccountPool();
      if (token !== poolReqRef.current) return;
      setPoolAccounts(page.accounts);
      setPoolError(null);
    } catch (err) {
      if (token !== poolReqRef.current) return;
      setPoolError(errMessage(err));
    } finally {
      if (token === poolReqRef.current) setPoolLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadPool();
  }, [loadPool]);

  // 「全部」時註冊預設跟著目前身分走；切到單一服務則由 onFilterChange 覆蓋。
  useEffect(() => {
    if (poolFilter === 'all') setPoolRegisterService(currentService);
  }, [currentService, poolFilter]);

  /**
   * 註冊 n 顆進帳號池（1–3）。倒數從**送出那一刻**就開始：後端在打站方前就記進 throttle，
   * 成功／失敗／429 都一樣要等 20 秒，先在本地鎖住按鈕體感才一致。
   */
  const registerPool = useCallback(
    (count: number) => {
      const n = Math.max(1, Math.min(MAX_POOL_REGISTER, Math.trunc(count)));
      /** 篩到單一服務時就以它為準；「全部」時用註冊區塊上選的那個。 */
      const service = poolFilter === 'all' ? poolRegisterService : poolFilter;
      setPoolRegistering(true);
      setPoolRegisterError(null);
      setPoolResults([]);
      setPoolCooldownUntil(Date.now() + POOL_REGISTER_GAP_MS);
      void (async () => {
        try {
          const result = await client.registerAccountPool({
            service,
            count: n,
            room: targetRoom,
          });
          setPoolResults(result.results);
          // #03：逐顆結果丟進佇列（一顆一張卡），不壓成一句話、也不互相蓋掉。
          // #03：逐顆結果丟進佇列（一顆一張卡），不壓成一句話、也不互相蓋掉。
          for (const item of poolRegisterNotices(result)) pushNotice(item);
          await loadPool();
        } catch (err) {
          const message = errMessage(err);
          setPoolRegisterError(message);
          setActionError(message);
        } finally {
          setPoolRegistering(false);
        }
      })();
    },
    [loadPool, poolFilter, poolRegisterService, pushNotice, setActionError, targetRoom],
  );

  /**
   * 用池內帳號進房（`loginMode:'account'` + `accountUsername`）。暱稱用帳號名，清單才看得出是哪一顆。
   * `ok:false`（上次登入失敗）**不擋**：可能只是當時被限流，原因照樣回在橫幅上。
   */
  const usePoolAccount = useCallback(
    async (target: PoolAccount | string) => {
      const username = (typeof target === 'string' ? target : target.username).trim();
      // 有帶 service 就用它；只給名字時優先挑目前身分那個站，避免兩個站同名互撞。
      const pooled: PoolAccount | undefined =
        typeof target === 'string'
          ? (poolAccounts.find(
              (item) => item.username === username && item.service === currentService,
            ) ?? poolAccounts.find((item) => item.username === username))
          : (poolAccounts.find(
              (item) => item.service === target.service && item.username === username,
            ) ?? target);
      if (!pooled) {
        const listing = poolAccounts
          .map((item) => `${item.service}:${item.username}`)
          .slice(0, 8)
          .join('  ');
        throw new Error(
          `帳號池沒有「${username}」${listing ? `；目前：${listing}` : '（池子是空的，先到右側「帳號」分頁註冊）'}`,
        );
      }
      const notice = pooled.ok
        ? `已用 ${pooled.username}（${pooled.service}）以帳號池模式登入並進 ${targetRoom}`
        : `${pooled.username}（${pooled.service}）上次登入失敗，這次仍試著登入（失敗原因會顯示在上方）`;
      // 建 session 的 service 一定跟著「這顆帳號」走，不是跟著目前身分。
      const ok = await runOpening({
        service: pooled.service,
        nickname: pooled.username,
        room: targetRoom,
        loginMode: 'account',
        accountUsername: pooled.username,
        notice,
      });
      void loadPool();
      // 失敗原因用**這次開通自己的錯誤**分類；不再寫「原因在上方」那種推測句。
      if (!ok) throw new Error(describeOpeningFailure(pooled.username, openingError?.() ?? null));
    },
    [currentService, loadPool, poolAccounts, runOpening, targetRoom],
  );

  /**
   * 改顯示名稱（帳號池 → 聊天室同步）。只打後端、不碰 session：`nickname`（登入名）不動，
   * 不會踢掉已在跑的連線。失敗（重名／長度）原樣往上拋，讓 `AccountPool` 那列顯示。
   */
  const renameAccount = useCallback(
    async (account: Account, displayName: string | null) => {
      if (typeof client.renameAccountPool !== 'function') {
        throw new Error('這個版本的前端不支援改名（請更新 console）');
      }
      // 帳號模式：`account`（歸因帳號）或建 session 時的 username；訪客沒有 username，不能改。
      const username =
        account.username ??
        (account as Conversation).account ??
        account.nickname;
      const updated = await client.renameAccountPool(account.service, username, displayName);
      setPoolAccounts((prev) =>
        prev.map((item) =>
          item.service === updated.service && item.username === updated.username
            ? { ...item, displayName: updated.displayName ?? null }
            : item,
        ),
      );
    },
    [],
  );

  /** 從池子移除一顆（確認已在面板內做完）。不動已在跑的 session。 */
  const deletePoolAccount = useCallback(
    (account: PoolAccount) => {
      setActionError(null);
      void (async () => {
        setBusy(true);
        try {
          await client.deleteAccountPool(account.service, account.username);
          setPoolResults((prev) => prev.filter((item) => item.username !== account.username));
          await loadPool();
          setNotice(`已從帳號池刪除 ${account.username}（已在跑的身分不受影響）`);
        } catch (err) {
          setActionError(errMessage(err));
        } finally {
          setBusy(false);
        }
      })();
    },
    [loadPool, setActionError, setBusy, setNotice],
  );

  /** 切服務篩選：切到單一服務時註冊就打這個站；切回「全部」保留上一次選擇（原本的副作用，原樣保留）。 */
  const changePoolFilter = useCallback((filter: PoolServiceFilter) => {
    setPoolFilter(filter);
    if (filter !== 'all') setPoolRegisterService(filter);
  }, []);

  return {
    sessionPicked,
    toggleSessionPick,
    joinSessions,
    leaveAll,
    leaveAllProgress,
    leaveAllResult,
    poolAccounts,
    poolLoading,
    poolError,
    poolRegistering,
    poolResults,
    poolRegisterError,
    poolCooldownUntil,
    poolFilter,
    setPoolFilter: changePoolFilter,
    poolRegisterService,
    setPoolRegisterService,
    loadPool,
    registerPool,
    usePoolAccount,
    deletePoolAccount,
    renameAccount,
  };
}
